// src/oura/components/docs/DocumentsBoard.tsx
//
// The staff documents board on the Client tab: 1 row per Google Doc in the
// client's folder, grouped by who moves next (Needs us, Waiting on client,
// Not sent yet, No code yet, then the quiet ones), or by code. Each group
// folds by its head; the quiet ones start folded. The group heads carry the
// counts, so there is no filter row repeating them. A row is 1 line and 1
// fact; the row opens the doc in the drawer, which holds the rest.
//
// Oura reads the folder. The Phase 1 writes are Pick a code (which files a
// doc, never renames it) and a doc's reply-by. With the Docs write switch on
// (DOCS_WRITE_ON), "Create a doc" on the card, and "Create" on a doc not sent
// yet or a code with no doc, open Create a doc (create/CreateDocSheet) in the
// same drawer; it leaves the new doc unshared. A doc's write views
// (write/: questions, name and lines, share, review email, chaser) open over
// it as sub-views, the way Pick a code does, with "‹ {doc title}" back.
//
// The screen holds the read (useBusinessDocuments) and which doc is open, so
// the page sentence and a `?doc=` link agree with the board.
//
// One drawer for every doc (drawer specs v1.1): a row opens with its group,
// the rows of its GroupFold (or By code block) in on-screen order as they
// were then, so previous and next (J and K) walk that group. A `?doc=` link
// opens with the doc's current group. "Pick a code" is a view of the same
// sheet: from the doc's foot it is a sub-view with a way back; from a row's
// own "Pick a code" it opens straight there.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertTriangleIcon, FileTextIcon, Link2Icon, Loader2Icon, MailIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { OURA_ROOT } from "../../nav";
import { DELIVERY_ON } from "../../delivery/flag";
import type { BusinessDocuments, CodeRow, StaffDocument } from "@service/aeo/clientDocs";
import { cn } from "../../lib/cn";
import { DOC_STATUS, Drawer, FamilyChip, FoldCard, GroupFold, INSET_HAIRLINE, LATE_TEXT, PillButton, SURFACE, Seg, ShowMore, TYPE, Tag, drawerNav, drawerSequence, useFoldOpener } from "@oura/ui";
import { DUE_BY_NOTE, agoText, codeDueText, codeFamily, codeLabel, codeRowName, docsFailureText, docsForRow, fromAgoText, isCodeOverdue, isReadStale, needUsText, plural, type DocsLoad } from "@oura/docs";
import { expectedDocFor } from "@service/aeo/clientDocsRules";
import { BOARD_GROUP, BOARD_GROUPS, docSheetTitle, docToSendName, groupBoard, groupCount, groupOfDoc, lateCodeRows, rowAmberFlag, useNow, type BoardGroupKey, type BoardGroups } from "./boardModel";
import { DocRow, LoadingRows, NotSentRow } from "./DocRow";
import { DocSheet } from "./DocDrawer";
import { FOLDER_LINK_FIELD_ID } from "./FolderCard";
import { lastLineFor, nextLive, type SheetFlow } from "../client/drawerFlow";
import { CreateDocSheet } from "./create/CreateDocSheet";
import { ConvertWordButton, CreateDocButton, CreateRowButton } from "./create/CreateDocButton";
import { ConvertWordSheet } from "./create/ConvertWordSheet";
import { DOCS_WRITE_ON } from "../../docs/writeFlag";
import { MultiEmailDialog } from "./write/MultiEmailDialog";
import { prefillFromCode, prefillFromToSend, EMPTY_PREFILL, type CreatePrefill } from "./create/createModel";
import type { DocWriteView } from "./write/model";
import { createRequestFor, type CreateRequest } from "./createRequest";

type View = "status" | "code";

/**
 * The group a doc was opened from, as it stood then: its rows in on-screen
 * order, the word the drawer says ("Needs us", "S4"), and how to tell whether
 * a doc is still in it (a status group, or a code's block in By code).
 */
interface DocSnap {
    ids: string[];
    label: string;
    group: { kind: "status"; key: BoardGroupKey } | { kind: "code"; code: string; outlet: string | null };
}

type OpenDoc = (id: string, snap: DocSnap) => void;
/** Open Create a doc, filled in for a doc the plan wants. */
type OpenCreate = (prefill: CreatePrefill) => void;

/** A status group's snapshot: every row in it, "Show N more" included. */
function statusSnap(groups: BoardGroups, key: Exclude<BoardGroupKey, "not_sent_yet">): DocSnap {
    return { ids: groups.docs[key].map((d) => d.id), label: BOARD_GROUP[key].style.label, group: { kind: "status", key } };
}

const AMBER_BLOCK = "rounded-2xl bg-amber-50 px-4 py-3 text-amber-800 ring-1 ring-inset ring-amber-200";

/**
 * An amber block inside the card: a failed read, a folder Oura cannot open.
 * Never a 0 in its place. A failed read is announced; a standing folder
 * condition is a polite status.
 */
function Notice({ children, action, role = "alert" }: { children: ReactNode; action?: ReactNode; role?: "alert" | "status" }) {
    return (
        <div role={role} className={cn(TYPE.tb, AMBER_BLOCK, "mb-3 flex items-start gap-2")}>
            <AlertTriangleIcon aria-hidden size={16} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
                {children}
                {action && <div className="mt-2">{action}</div>}
            </div>
        </div>
    );
}

type Counts = NonNullable<BusinessDocuments["counts"]>;

/**
 * The folded card's words: who moves next, short enough to sit beside the
 * title on a phone. Late is its own part: a doc that needs us can be late
 * too, so it is never a part of the waiting docs. The page sentence says the
 * totals once; open, the group heads say their own.
 */
function countParts(c: Counts, lateCodes: number): ReactNode[] {
    // A code not sent by its due date is late too (YW, 29 Sep 2026).
    const late = [c.late > 0 && plural(c.late, "doc"), lateCodes > 0 && plural(lateCodes, "code")].filter(Boolean).join(" and ");
    return [
        c.needsUs > 0 && needUsText(c.needsUs),
        late && <span className={LATE_TEXT}>{late} late</span>,
        c.waitingOnClient > 0 && `${c.waitingOnClient} waiting`,
    ].filter(Boolean);
}

/** Parts joined by a middle dot. */
function Dotted({ parts }: { parts: ReactNode[] }) {
    return (
        <>
            {parts.map((p, i) => (
                <span key={i}>
                    {i > 0 && " · "}
                    {p}
                </span>
            ))}
        </>
    );
}

/**
 * What a folded group still has to say, so folding it never hides what
 * someone must act on: how many of its docs have a red flag, an amber flag
 * (the rows' tag), or are late. Docs, never flags: "1 with a red flag", as
 * the page sentence counts them. A doc that needs us is late on the client's
 * side, and says so. A doc a skill is still writing says so too, or a folded
 * Not shared hides it (#4391). Null when it has none.
 */
function groupNote(groups: BoardGroups, key: BoardGroupKey): ReactNode {
    const docs = key === "not_sent_yet" ? [] : groups.docs[key];
    const writing = docs.filter((d) => d.skillRun?.status === "running").length;
    const red = docs.filter((d) => d.flags.some((f) => f.tone === "red")).length;
    const amber = key === "not_sent_yet" ? 0 : docs.filter((d) => rowAmberFlag(d, key)).length;
    const late = key === "not_sent_yet" ? groups.toSend.filter((d) => d.late).length : docs.filter((d) => d.late).length;
    const parts = [
        writing > 0 && (
            <span className="inline-flex items-center gap-1 text-gray-600">
                <Loader2Icon aria-hidden size={12} className="animate-spin" />
                {writing} being written by Oura
            </span>
        ),
        red > 0 && <span className="font-medium text-[#C5221F]">{red} with a red flag</span>,
        amber > 0 && <span className="font-medium text-amber-700">{amber} to check</span>,
        late > 0 && <span className={LATE_TEXT}>{key === "needs_us" ? `${late} client late` : `${late} late`}</span>,
    ].filter(Boolean);
    return parts.length > 0 ? <Dotted parts={parts} /> : null;
}

/**
 * The docs of one group, the first few shown and the rest behind "Show N
 * more". The rest are bare siblings of the first few's box, so the hairline
 * above each row carries on across the join.
 */
function DocGroup({ docs, shown, snap, onOpen, onPick, onCreate, dueByDoc }: { docs: StaffDocument[]; shown: number | null; snap: DocSnap; onOpen: OpenDoc; onPick: OpenDoc; onCreate: OpenCreate; dueByDoc?: BoardGroups["dueByDoc"] }) {
    const first = shown === null ? docs : docs.slice(0, shown);
    const rest = shown === null ? [] : docs.slice(shown);
    const open = (id: string) => onOpen(id, snap);
    const pick = (id: string) => onPick(id, snap);
    return (
        <>
            <div>
                {first.map((d) => (
                    <DocRow key={d.id} doc={d} canMarkDone canDelete={DOCS_WRITE_ON} onOpen={open} onPick={pick} onCreate={onCreate} due={dueByDoc?.get(d.id) ?? null} />
                ))}
            </div>
            <ShowMore count={rest.length}>
                {rest.map((d) => (
                    <DocRow key={d.id} doc={d} canMarkDone canDelete={DOCS_WRITE_ON} onOpen={open} onPick={pick} onCreate={onCreate} due={dueByDoc?.get(d.id) ?? null} />
                ))}
            </ShowMore>
        </>
    );
}

function ByStatus({ groups, onOpen, onPick, onCreate }: { groups: BoardGroups; onOpen: OpenDoc; onPick: OpenDoc; onCreate: OpenCreate }) {
    // The groups are the only children of this box, so the first loses its divider.
    return (
        <div>
            {BOARD_GROUPS.map((g) => {
                const count = groupCount(groups, g.key);
                if (count === 0) return null;
                return (
                    <GroupFold
                        key={g.key}
                        id={`documents.${g.key}`}
                        lead="square"
                        rowEnd="open"
                        tone={g.style.tone}
                        title={g.style.label}
                        count={count}
                        defaultFolded={!g.open}
                        note={groupNote(groups, g.key)}
                    >
                        {g.key === "not_sent_yet" ? (
                            // 1 row per doc to send, with the codes it clears.
                            <div>
                                {/* "Not sent yet" reads as files that are missing from the folder. These are docs
                                    the PLAN expects by now that nobody has made: say so, once, above the rows. */}
                                <p className={cn(TYPE.tc, "m-0 pb-2 pl-11 text-gray-500")}>
                                    The plan expects these docs by now and none has been made yet. They are not files in the folder.
                                </p>
                                {groups.toSend.map((d) => (
                                    <NotSentRow
                                        key={d.key}
                                        doc={d}
                                        action={DOCS_WRITE_ON && d.docType ? <CreateRowButton name={docToSendName(d)} onClick={() => onCreate(prefillFromToSend(d))} /> : undefined}
                                    />
                                ))}
                            </div>
                        ) : (
                            <DocGroup docs={groups.docs[g.key]} shown={g.shown} snap={statusSnap(groups, g.key)} onOpen={onOpen} onPick={onPick} onCreate={onCreate} dueByDoc={groups.dueByDoc} />
                        )}
                    </GroupFold>
                );
            })}
        </div>
    );
}

/** The head of a code's group: its chip, the contract's name for it, how many docs, and its state. */
function CodeHead({ row, count, businessId, create }: { row: CodeRow; count: number; businessId?: string; create?: ReactNode }) {
    const label = codeLabel(row.code, row.outlet);
    // A state this build does not know shows no tag, never a crash.
    const state = DOC_STATUS[row.state]?.staff ?? null;
    return (
        <div className="flex min-h-10 flex-wrap items-center gap-2">
            <FamilyChip family={codeFamily(row.code)}>{label}</FamilyChip>
            {/* The flag decides: a null label only means the deliverable has no name. */}
            <span className={cn(TYPE.tc, "min-w-0 break-words font-semibold text-gray-900")}>{row.inContract ? (row.label ? codeRowName(row.label, row.outlet) : row.code) : "Not in the contract"}</span>
            <span className={cn(TYPE.tc, "tabular-nums text-gray-400")}>{count}</span>
            {state && (
                <span className="ml-auto">
                    <Tag tone={state.tone}>{state.label}</Tag>
                </span>
            )}
            {/* By code is Delivery's view of the docs now: each code opens its deliverable there. */}
            {DELIVERY_ON && businessId && row.inContract && (
                <Link
                    to={`${OURA_ROOT}/businesses/${encodeURIComponent(businessId)}/delivery${row.expectedMonth && !row.outlet ? `?d=${encodeURIComponent(`${row.code}-m${row.expectedMonth}`)}` : ""}`}
                    className={cn(TYPE.tc, state ? "" : "ml-auto", "font-medium text-blue-700 hover:underline")}
                >
                    Open in Delivery
                </Link>
            )}
            {create}
        </div>
    );
}

function ByCode({
    board,
    groups,
    onOpen,
    onPick,
    onCreate,
    businessId,
}: {
    board: BusinessDocuments;
    groups: BoardGroups;
    onOpen: OpenDoc;
    onPick: OpenDoc;
    onCreate: OpenCreate;
    businessId?: string;
}) {
    // Codes with a doc, or owed one, lead; the rest wait in a folded group.
    const withDocs = board.codes.filter((r) => r.documentIds.length > 0 || r.state === "not_sent_yet");
    const idle = board.codes.filter((r) => r.documentIds.length === 0 && r.state !== "not_sent_yet");
    // Every block and group is a child of this box, so the first loses its divider.
    return (
        <div>
            {withDocs.map((r) => {
                const docs = docsForRow(board, r);
                const snap: DocSnap = { ids: docs.map((d) => d.id), label: codeLabel(r.code, r.outlet), group: { kind: "code", code: r.code, outlet: r.outlet ?? null } };
                // A code owed a doc says when it is due, even when its docs exist but are not shared.
                const due = docs.length === 0 || r.state === "not_sent_yet" ? codeDueText(r, true) : null;
                // The doc it goes out in, from the catalogue, so the block says what to send.
                const sendIn = r.state === "not_sent_yet" ? expectedDocFor(r.code)?.docType ?? null : null;
                const shared = docs.length === 0
                    ? "Nothing shared yet"
                    : r.expectedDocs !== null && r.expectedDocs > 1
                        ? `${r.sharedDocs} of ${r.expectedDocs} shared`
                        : "Not shared with the client yet";
                const note = (docs.length === 0 || due) && (
                    <p className={cn(TYPE.tc, "m-0 pb-1 text-gray-500")}>
                        {shared}
                        {sendIn && ` · Goes out in the ${sendIn}`}
                        {due && (
                            <>
                                {" · "}
                                <span title={DUE_BY_NOTE} className={cn(isCodeOverdue(r) && LATE_TEXT)}>
                                    {due}
                                </span>
                            </>
                        )}
                    </p>
                );
                return (
                    <div key={`${r.code}-${r.outlet ?? ""}`} className={cn("border-t pb-2 first:border-t-0", SURFACE.divider)}>
                        <CodeHead row={r} count={docs.length} businessId={businessId} create={docs.length === 0 && createFor(r, onCreate)} />
                        {note}
                        {docs.length > 0 && (
                            <div>
                                {docs.map((d) => (
                                    <DocRow key={d.id} doc={d} canMarkDone canDelete={DOCS_WRITE_ON} onOpen={(id) => onOpen(id, snap)} onPick={(id) => onPick(id, snap)} onCreate={onCreate} showStatus />
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
            {groups.docs.no_code.length > 0 && (
                // The same fold as By status: open in one view, open in the other.
                <GroupFold
                    id="documents.no_code"
                    lead="square"
                    rowEnd="open"
                    tone={BOARD_GROUP.no_code.style.tone}
                    title={BOARD_GROUP.no_code.style.label}
                    count={groups.docs.no_code.length}
                    defaultFolded={!BOARD_GROUP.no_code.open}
                    note={groupNote(groups, "no_code")}
                >
                    <div>
                        {groups.docs.no_code.map((d) => (
                            <DocRow key={d.id} doc={d} canMarkDone canDelete={DOCS_WRITE_ON} onOpen={(id) => onOpen(id, statusSnap(groups, "no_code"))} onPick={(id) => onPick(id, statusSnap(groups, "no_code"))} onCreate={onCreate} />
                        ))}
                    </div>
                </GroupFold>
            )}
            {idle.length > 0 && (
                <GroupFold id="documents.codes_idle" lead="dot" tone="gray" title="Codes with no doc" count={idle.length} defaultFolded>
                    <div>
                        {idle.map((r) => {
                            const due = codeDueText(r);
                            const create = createFor(r, onCreate);
                            return (
                                // Lined up with the group's title, as a dot-led row's text is.
                                <div key={`${r.code}-${r.outlet ?? ""}`} className={cn("flex min-h-11 items-center gap-2 py-2 pl-5", INSET_HAIRLINE("dot"))}>
                                    <FamilyChip family={codeFamily(r.code)}>{codeLabel(r.code, r.outlet)}</FamilyChip>
                                    <span className={cn(TYPE.tc, "min-w-0 flex-1 break-words text-gray-600")}>{r.label ? codeRowName(r.label, r.outlet) : r.code}</span>
                                    <span title={due ? DUE_BY_NOTE : undefined} className={cn(TYPE.tc, "shrink-0 tabular-nums text-gray-400")}>
                                        {due ?? (create ? "No due date" : "No doc expected")}
                                    </span>
                                    {create}
                                </div>
                            );
                        })}
                    </div>
                </GroupFold>
            )}
        </div>
    );
}

/**
 * "Create" on a code row with no doc, when a catalogue doc covers the code; null otherwise.
 * Null with the write switch off, so the row reads as Phase 1's ("No doc expected").
 */
function createFor(r: CodeRow, onCreate: OpenCreate): ReactNode {
    if (!DOCS_WRITE_ON) return null;
    const prefill = prefillFromCode(r.code, r.outlet ?? null);
    if (!prefill?.docType) return null;
    return <CreateRowButton name={`${prefill.docType} (${codeLabel(r.code, r.outlet)})`} onClick={() => onCreate(prefill)} />;
}

/**
 * A doc's row, or when its group is folded (a doc just filed into Not shared,
 * or marked internal), that group's head. A row that moved group re-renders
 * elsewhere: it is found again by its id.
 */
function findRow(id: string, docById: ReadonlyMap<string, StaffDocument>): HTMLElement | null {
    const sel = CSS.escape(id);
    const doc = docById.get(id);
    return (
        document.querySelector<HTMLElement>(`[data-doc-open="${sel}"]`) ??
        document.querySelector<HTMLElement>(`[data-doc-pick="${sel}"]`) ??
        (doc ? document.querySelector<HTMLElement>(`[data-group-head="${CSS.escape(`documents.${groupOfDoc(doc)}`)}"]`) : null)
    );
}

export function DocumentsBoard({
    load,
    businessId,
    businessName,
    openDocId,
    onOpenDoc,
    createRequest = null,
    onCreateTaken,
}: {
    load: DocsLoad<BusinessDocuments>;
    /** For "Read the folder again" in the drawer. */
    businessId: string;
    businessName: string;
    /** The doc open in the drawer, held by the screen so a `?doc=` link can open one. */
    openDocId: string | null;
    onOpenDoc: (id: string | null) => void;
    /**
     * A `?create=` link (Ask Oura, preview): open Create a doc with this doc
     * type picked, or with nothing picked for "new". Taken once the board is read,
     * and only on the client it was made on: another client's request is dropped.
     */
    createRequest?: CreateRequest | null;
    onCreateTaken?: () => void;
}) {
    const openCard = useFoldOpener();
    const now = useNow();
    const [view, setView] = useState<View>("status");
    /** The group the open doc came from, as it stood then. */
    const [snap, setSnap] = useState<DocSnap | null>(null);
    /** Pick a code over the open doc: from its foot (a sub-view) or from its row (straight there). */
    /** Pick a code, or a write view (questions, name, share, emails), over the open doc. */
    const [sheetView, setSheetView] = useState<{ id: string; mode: "doc" | "pick" | DocWriteView; entry: "foot" | "row" } | null>(null);
    /** A write runs in the sheet: previous and next wait. */
    const [busy, setBusy] = useState(false);
    /** Create a doc, open in the drawer in place of a doc; `n` re-keys it for Next. */
    const [create, setCreate] = useState<{ prefill: CreatePrefill; n: number } | null>(null);
    /** "Convert Word files" open in the drawer (#4328). */
    const [convertAll, setConvertAll] = useState(false);
    /** The doc last open: focus goes back to its row after the drawer has closed and the screen has let go of it. */
    const lastOpen = useRef<string | null>(null);
    if (openDocId) lastOpen.current = openDocId;

    const board = load.data;
    const [multiEmail, setMultiEmail] = useState(false);
    const groups = useMemo(() => (board ? groupBoard(board) : null), [board]);
    const read = Boolean(board?.folder && board.counts);
    const docById = useMemo(() => new Map((board?.documents ?? []).map((d) => [d.id, d] as const)), [board]);

    const c = board?.counts ?? null;
    // Docs only: "out" counts questions, never docs.
    const folded = c && board ? countParts(c, lateCodeRows(board).length) : [];
    // A folder Oura can no longer read keeps its last good counts: folded,
    // the card says it cannot read rather than show them as current.
    const foldedFact = !board || load.status !== "ready"
        ? null
        : !board.folder
            ? "Folder not linked"
            : board.folder.lastError
                ? "Can't read the folder"
                : c
                    ? folded.length > 0
                        ? <Dotted parts={folded} />
                        : board.documents.length === 0 ? "No files yet" : "Nothing waiting"
                    : "Not read yet";

    // Nothing waits on either side when no group someone waits in holds a row.
    const waiting = groups ? BOARD_GROUPS.some((g) => g.waits && groupCount(groups, g.key) > 0) : true;

    // Word files no Google Doc was made from yet: what "Convert Word files" offers.
    const unconvertedWord = (board?.documents ?? []).filter((d) => d.fileType === "word" && !d.notClientDoc && !d.convertedTo).length;

    const openDoc: OpenDoc = (id, from) => {
        setSnap(from);
        setSheetView(null);
        setCreate(null);
        setConvertAll(false);
        onOpenDoc(id);
    };
    const openCreate: OpenCreate = (prefill) => {
        lastOpen.current = null;
        setSheetView(null);
        setBusy(false);
        setCreate((c) => ({ prefill, n: (c?.n ?? 0) + 1 }));
        setConvertAll(false);
        if (openDocId) onOpenDoc(null);
    };
    const openConvertAll = () => {
        lastOpen.current = null;
        setSheetView(null);
        setBusy(false);
        setCreate(null);
        setConvertAll(true);
        if (openDocId) onOpenDoc(null);
    };
    // A `?create=` link opens the sheet once the board is read, with the doc type picked.
    // A request made on another client is dropped unopened, whatever this board shows.
    useEffect(() => {
        if (!createRequest) return;
        const mine = createRequestFor(createRequest, businessId);
        if (!mine) {
            onCreateTaken?.();
            return;
        }
        if (!board || !DOCS_WRITE_ON) return;
        openCreate({ docType: mine.docType, codes: mine.codes, outlet: mine.outlet });
        onCreateTaken?.();
        // openCreate and onCreateTaken are rebuilt every render; the request and the client are what this runs on.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createRequest, board, businessId]);
    /** From Create to a doc in the same drawer: the one just made, or the one a refusal names. */
    const createToDoc = (id: string) => {
        setCreate(null);
        setSheetView(null);
        setBusy(false);
        onOpenDoc(id);
    };
    const openPick: OpenDoc = (id, from) => {
        setSnap(from);
        setSheetView({ id, mode: "pick", entry: "row" });
        setCreate(null);
        onOpenDoc(id);
    };
    const linkFolder = () => {
        openCard("folder");
        // After the card has unfolded, the cursor goes in the link field.
        window.setTimeout(() => document.getElementById(FOLDER_LINK_FIELD_ID)?.focus(), 350);
    };
    // A doc opened by a `?doc=` link came from no row: it walks its current group.
    const openBoardDoc = openDocId ? docById.get(openDocId) ?? null : null;
    if (openDocId && groups && openBoardDoc && !snap?.ids.includes(openDocId)) {
        const key = groupOfDoc(openBoardDoc);
        setSnap(statusSnap(groups, key));
    }

    /** Whether a doc is still in the group it was opened from. */
    const inSnap = (id: string): boolean => {
        const d = docById.get(id);
        if (!d || !snap) return false;
        if (snap.group.kind === "status") return groupOfDoc(d) === snap.group.key;
        const { code, outlet } = snap.group;
        return Boolean(board?.codes.find((r) => r.code === code && (r.outlet ?? null) === outlet)?.documentIds.includes(id));
    };
    /** A doc's title while it is still in the group; null skips it (it moved, or left the folder). */
    const titleOf = (id: string) => {
        const d = docById.get(id);
        return d && inSnap(id) ? docSheetTitle(d) : null;
    };

    const closeSheet = () => {
        setSheetView(null);
        setBusy(false);
        setCreate(null);
        setConvertAll(false);
        onOpenDoc(null);
    };
    const moveTo = (id: string) => {
        setSheetView(null);
        setBusy(false);
        onOpenDoc(id);
    };
    const mode = openDocId && sheetView?.id === openDocId ? sheetView.mode : "doc";
    const entry = openDocId && sheetView?.id === openDocId ? sheetView.entry : "foot";
    const ids = snap?.ids ?? [];
    const seq = openDocId ? drawerSequence(ids, openDocId, titleOf) : null;
    const nav = seq && snap && mode === "doc" && !create ? drawerNav(seq, { group: snap.label, onMove: moveTo, keys: true, disabled: busy }) : undefined;
    const next = openDocId ? nextLive(ids, openDocId, titleOf) : null;
    const flow: SheetFlow = {
        inGroup: openDocId ? inSnap(openDocId) : false,
        group: snap?.label ?? "",
        left: seq?.left ?? 0,
        next,
        onNext: () => {
            if (next) moveTo(next.id);
        },
        onDone: closeSheet,
        lastLine: snap ? lastLineFor(snap.label) : undefined,
        onBusy: setBusy,
        onOpenDoc: moveTo,
        onCreate: openCreate,
    };

    let body: ReactNode;
    if (load.status === "loading") {
        body = (
            <>
                <p className="sr-only" role="status">
                    Loading the documents
                </p>
                <LoadingRows />
            </>
        );
    } else if (!board) {
        body = (
            <Notice action={load.error?.reason === "notServed" ? undefined : <PillButton size="sm" onClick={load.reload}>Try again</PillButton>}>
                {load.error ? docsFailureText(load.error) : "Could not read the documents."}
            </Notice>
        );
    } else if (!board.folder) {
        body = (
            <>
                <p className={cn(TYPE.tb, "m-0 text-gray-600")}>The client&rsquo;s folder is not linked yet. Oura reads the docs once it is.</p>
                <PillButton className="mt-3" variant="primary" icon={Link2Icon} onClick={linkFolder}>
                    Link the folder
                </PillButton>
            </>
        );
    } else {
        const folder = board.folder;
        body = (
            <>
                {load.error && (
                    <Notice>
                        {docsFailureText(load.error)} {fromAgoText(load.loadedAt, now) && `These rows are from ${fromAgoText(load.loadedAt, now)}.`}
                    </Notice>
                )}
                {folder.lastError && (
                    <Notice role="status">
                        Oura could not read the folder. {folder.lastError.message}
                        {board.readAt ? ` The rows are from its last good read, ${agoText(board.readAt, now)}.` : ""}
                    </Notice>
                )}
                {!read ? (
                    !folder.lastError && (
                        <p className={cn(TYPE.tb, "m-0 flex items-center gap-2 text-gray-600")}>
                            <Loader2Icon aria-hidden size={16} className="animate-spin motion-reduce:animate-none" />
                            Oura is reading the folder for the first time.
                        </p>
                    )
                ) : (
                    <>
                        {/* A healthy read says nothing here: the Client folder card
                            has "Read 4 min ago". Only reads that stopped are news. */}
                        {!folder.lastError && isReadStale(board.readAt, 5, now) && (
                            <Notice role="status">Oura last read the folder {agoText(board.readAt, now)}. Reads have stopped.</Notice>
                        )}
                        {board.documents.length === 0 && (
                            <p className={cn(TYPE.tb, "m-0 mb-3 text-gray-600")}>
                                The folder has no files yet.
                                {groups && groups.toSend.length > 0 ? " The docs listed below are what the plan expects, so nothing has gone missing." : ""}
                            </p>
                        )}
                        {view === "status" && board.documents.length > 0 && !waiting && (
                            <p className={cn(TYPE.tb, "m-0 mb-2 text-gray-500")}>Nothing waits on either side.</p>
                        )}
                        {groups && view === "status" && <ByStatus groups={groups} onOpen={openDoc} onPick={openPick} onCreate={openCreate} />}
                        {groups && view === "code" && <ByCode board={board} groups={groups} onOpen={openDoc} onPick={openPick} onCreate={openCreate} businessId={businessId} />}
                    </>
                )}
            </>
        );
    }


    return (
        <>
            <FoldCard
                id="documents"
                anchor="documents"
                title="Documents"
                icon={{ icon: FileTextIcon, area: "setup" }}
                foldedFact={foldedFact}
                right={
                    read && board && (
                        <span className="flex flex-wrap items-center justify-end gap-2">
                            {unconvertedWord > 0 && <ConvertWordButton count={unconvertedWord} onClick={openConvertAll} />}
                            <CreateDocButton onClick={() => openCreate(EMPTY_PREFILL)} />
                            {/* One email linking several docs (3 Nov 2026), on the write switch like Create a doc. */}
                            {DOCS_WRITE_ON && (
                                <PillButton size="sm" variant="primary" icon={MailIcon} onClick={() => setMultiEmail(true)}>
                                    Send Email
                                </PillButton>
                            )}
                            <Seg<View>
                                size="sm"
                                ariaLabel="Group the documents"
                                value={view}
                                onChange={setView}
                                className="flex-wrap"
                                items={[
                                    { key: "status", label: "By status" },
                                    { key: "code", label: "By code" },
                                ]}
                            />
                        </span>
                    )
                }
            >
                {body}
            </FoldCard>

            {multiEmail && board && <MultiEmailDialog businessId={businessId} documents={board.documents} onClose={() => setMultiEmail(false)} />}

            {/* One shell; the sheet inside is keyed per doc, so its own state resets on a move. */}
            <Drawer
                open={Boolean(openDocId) || Boolean(create) || convertAll}
                onClose={closeSheet}
                nav={nav}
                back={
                    !create && openDocId && mode !== "doc" && entry === "foot"
                        ? { label: openBoardDoc ? docSheetTitle(openBoardDoc) : "Document", onBack: () => setSheetView({ id: openDocId, mode: "doc", entry: "foot" }) }
                        : undefined
                }
                itemKey={convertAll ? "convert-word" : create ? `create:${create.n}` : openDocId ? `${openDocId}:${mode}` : undefined}
                // The row, found again by its id in whatever group it now sits
                // in; or that group's head when it is folded; else the opener.
                returnFocus={() => (lastOpen.current ? findRow(lastOpen.current, docById) : null)}
            >
                {convertAll ? (
                    // keyed by the client: its plan, results and progress never carry over to another (Jack, #4329)
                    <ConvertWordSheet key={`convert-word:${businessId}`} businessId={businessId} onClose={closeSheet} onBusy={setBusy} />
                ) : create && board ? (
                    <CreateDocSheet
                        key={`create:${create.n}`}
                        businessId={businessId}
                        businessName={businessName}
                        board={board}
                        prefill={create.prefill}
                        onOpenDoc={createToDoc}
                        onNext={openCreate}
                        onClose={closeSheet}
                        onBusy={setBusy}
                    />
                ) : openDocId && (
                    <DocSheet
                        key={openDocId}
                        docId={openDocId}
                        boardDoc={openBoardDoc}
                        board={board}
                        businessId={businessId}
                        businessName={businessName}
                        mode={mode}
                        onPick={() => setSheetView({ id: openDocId, mode: "pick", entry: "foot" })}
                        onPickCancel={() => (entry === "row" ? closeSheet() : setSheetView({ id: openDocId, mode: "doc", entry: "foot" }))}
                        onPickSaved={() => setSheetView({ id: openDocId, mode: "doc", entry: "foot" })}
                        onView={(v) => setSheetView({ id: openDocId, mode: v, entry: "foot" })}
                        flow={flow}
                    />
                )}
            </Drawer>
        </>
    );
}
